"use client";

import { LockClosedIcon } from "@heroicons/react/20/solid";
import { notFound } from "next/navigation";
import AnalysisDetails from "~/src/components/feature/analysis/info/AnalysisDetails";
import { QUERY_PARAM_VALUE_POST } from "~/src/lib/ui/transition";
import { useAuthContext } from "~/src/stores/context/auth/AuthContext";
import { AnalysisDocument } from "~/src/type/firebase/firestore";
import { GraphInfo } from "~/src/type/ui/article/article";

type AnalysisDetailsGateProps = {
  analysisId: string;
  contents: AnalysisDocument;
  graph: GraphInfo[];
  from: string | null;
};

/**
 * 分析記事詳細ページ - 認証依存の表示制御
 *   ・投稿直後遷移(from=post)のURL整合性検証
 *   ・非公開記事は本人のみ閲覧可
 *   ・いいね状態の解決
 * @note 公開記事（permissionUser && permissionAdmin）は認証状態に依存せず
 *       本文をそのまま表示するため、サーバーレンダリング結果にも記事本文が含まれる
 */
export default function AnalysisDetailsGate({
  analysisId,
  contents,
  graph,
  from,
}: AnalysisDetailsGateProps) {
  const { user, info } = useAuthContext();

  const isOwn = user ? user.uid === contents.userId : false;
  const isPublic = contents.permissionUser && contents.permissionAdmin;

  //------------
  // URL整合性検証
  //------------
  // 投稿直後遷移のクエリパラメータは、本人自身の記事に対してのみ許可する
  if (from === QUERY_PARAM_VALUE_POST && user !== undefined && !isOwn) {
    notFound();
  }

  //------------
  // 非公開記事
  //------------
  // 本人以外には非公開UIを表示する（認証状態解決前は安全側として非公開扱いにする）
  if (!isPublic && !isOwn) {
    return (
      <div className="min-h-[600px] py-4 md:pt-6">
        <div className="m-auto mt-10 flex max-w-md flex-col items-center justify-center gap-3 rounded-xl bg-GRAY_3 p-6 text-center">
          <LockClosedIcon
            aria-hidden="true"
            className="size-8 text-GRAY_1"
          />
          <p className="text-lg font-semibold text-GRAY_1">この記事は現在非公開です</p>
          <p className="text-sm text-GRAY_1">公開設定が変更されるまで閲覧できません</p>
        </div>
      </div>
    );
  }

  const liked = user && info ? info.favoritList.includes(analysisId) : false;

  return (
    <AnalysisDetails
      analysisId={analysisId}
      contents={contents}
      graph={graph}
      analysisTime={{ create: contents.createdAt, update: contents.updatedAt }}
      liked={liked}
    />
  );
}
